import {Search, X} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listSimLaunches } from '@/lib/launchpad/store'
import { listSimPresales } from '@/lib/presale/store'
import { useTokenImage } from '@/lib/projectLinks'
import { searchTokens, type TokenSearchResult } from '@/lib/tokenSearch'

function TokenAvatar({ result }: { result: TokenSearchResult }) {
  const imageUrl = useTokenImage(
    [result.contractAddress, result.shareSlug, result.id, result.tokenAddress],
    result.imageUrl,
  )
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={`${result.name} token art`}
        className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
      />
    )
  }
  return (
    <div
      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
        result.kind === 'curve' ? 'bg-lime-400/15 text-lime-400' : 'bg-orange-400/15 text-orange-400'
      }`}
    >
      {result.ticker.slice(0, 2)}
    </div>
  )
}

function NavbarTokenSearch({ className = '' }: { className?: string }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)

  const results = useMemo(() => {
    if (!query.trim())
      return []
    return searchTokens(query, listSimLaunches(), listSimPresales())
  }, [query])

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node))
        setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  function goTo(result: TokenSearchResult) {
    navigate(result.path)
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && results.length > 0) {
              event.preventDefault()
              goTo(results[0])
            }
            if (event.key === 'Escape')
              setOpen(false)
          }}
          placeholder="Search for a token"
          aria-label="Search for a token"
          className="w-full rounded-full border border-white/10 bg-white/[0.04] py-2 pl-9 pr-9 text-sm text-white placeholder:text-zinc-500 focus:border-lime-400/50 focus:outline-none focus:ring-1 focus:ring-lime-400/40"
        />
        {query
          ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('')
                  setOpen(false)
                }}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-zinc-500 hover:text-white"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )
          : null}
      </div>

      {open && query.trim()
        ? (
            <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-white/10 bg-black/95 p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.55)] backdrop-blur-xl">
              {results.length === 0
                ? (
                    <p className="px-3 py-3 text-sm text-zinc-500">No tokens found for &quot;{query}&quot;</p>
                  )
                : (
                    results.map(result => (
                      <button
                        key={`${result.kind}-${result.id}`}
                        type="button"
                        onClick={() => goTo(result)}
                        className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-white/5"
                      >
                        <TokenAvatar result={result} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-white">{result.name}</span>
                          <span className="block truncate text-xs text-zinc-500">
                            $
                            {result.ticker}
                          </span>
                        </span>
                        <span
                          className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                            result.kind === 'curve' ? 'bg-lime-400/10 text-lime-400' : 'bg-orange-400/10 text-orange-400'
                          }`}
                        >
                          {result.kind === 'curve' ? 'Launch' : 'Presale'}
                        </span>
                      </button>
                    ))
                  )}
            </div>
          )
        : null}
    </div>
  )
}

export default NavbarTokenSearch
